import { useCallback, useContext, useEffect, useState } from 'react';

import { ChatMessageListScrollContext } from './ChatMessageListScrollContext';

/**
 * True when the group's sticky header is pinned (sentinel has scrolled above the list top).
 * Blur/tint should only apply in this state.
 */
export function useChatStickyHeaderPinned(
  sentinelRef: React.RefObject<HTMLElement | null>,
): boolean {
  const scrollRef = useContext(ChatMessageListScrollContext);
  const [pinned, setPinned] = useState(false);

  const update = useCallback(() => {
    const scrollEl = scrollRef?.current;
    const sent = sentinelRef.current;
    if (!scrollEl || !sent) return;
    const rt = scrollEl.getBoundingClientRect();
    const st = sent.getBoundingClientRect();
    const viewportTop = rt.top + scrollEl.clientTop;
    setPinned(st.bottom < viewportTop);
  }, [scrollRef, sentinelRef]);

  useEffect(() => {
    const scrollEl = scrollRef?.current;
    if (!scrollEl) return;
    const run = () => update();
    run();
    const raf = requestAnimationFrame(() => requestAnimationFrame(run));
    scrollEl.addEventListener('scroll', run, { passive: true });
    window.addEventListener('resize', run);
    const ro = new ResizeObserver(() => run());
    ro.observe(scrollEl);
    return () => {
      cancelAnimationFrame(raf);
      scrollEl.removeEventListener('scroll', run);
      window.removeEventListener('resize', run);
      ro.disconnect();
    };
  }, [scrollRef, update]);

  return pinned;
}
